import { CheckCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { BEHAVIOUR_LABELS } from '../../../common/constants';
import CommonTable from '../../../components/CommonTable';

/**
 * IncidentsListView renders a flat, one-row-per-incident table across all
 * employees, sourced from the attendanceIncidents query. The view icon opens
 * the Manage Incident modal; its Excuse/Archive tabs are only editable for
 * users with update permission (canUpdate), read-only otherwise.
 */
function IncidentsListView({
  data,
  loading,
  onChange,
  onEdit,
  canUpdate,
  currentUserId,
  paginationConfig,
}) {
  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      fixed: 'left',
      width: 200,
      ellipsis: true,
      render: (_, record) =>
        `${record.employee?.firstName || ''} ${
          record.employee?.lastName || ''
        }`.trim(),
    },
    {
      title: 'Incident Time',
      key: 'loggedAt',
      width: 170,
      render: (_, record) =>
        record.loggedAt
          ? dayjs(record.loggedAt).format('DD MMM YYYY, hh:mm A')
          : '—',
    },
    {
      title: 'Behaviour',
      key: 'behaviourKey',
      width: 200,
      render: (_, record) =>
        BEHAVIOUR_LABELS[record.behaviourKey] || record.behaviourKey,
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (value) => value || '—',
    },
    {
      title: 'Count',
      dataIndex: 'scoreImpact',
      key: 'scoreImpact',
      align: 'center',
      width: 80,
      render: (value) => value || '—',
    },
    {
      title: 'Genuine',
      key: 'isExcused',
      align: 'center',
      width: 80,
      render: (_, record) =>
        record.excusedCount > 0 ? (
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
        ) : (
          '—'
        ),
    },
    {
      title: 'Status',
      key: 'isArchived',
      align: 'center',
      width: 100,
      render: (_, record) =>
        record.isArchived ? (
          <Tag color="default">Archived</Tag>
        ) : (
          <Tag color="green">Active</Tag>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      fixed: 'right',
      width: 90,
      render: (_, record) => {
        // A user cannot edit their own incident, even with update permission.
        const isOwnIncident =
          !!currentUserId && record?.employee?.id === currentUserId;
        const canEditRecord = canUpdate && !isOwnIncident;
        return (
          <Tooltip title={canEditRecord ? 'View / Edit' : 'View'}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onEdit && onEdit(record)}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <CommonTable
      size="small"
      bordered
      columns={columns}
      data={data || []}
      loadingData={loading}
      rowKey={(obj) => obj?.id}
      scroll={{ x: 1200, y: '100%' }}
      onChange={onChange}
      paginationConfig={paginationConfig}
    />
  );
}

export default IncidentsListView;
